import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Briefcase, Scale, Wrench, IndianRupee, Zap, Link2, RefreshCw, LayoutGrid, GitBranch } from 'lucide-react';
import { toast } from 'sonner';
import TimelineView from './DealGroupTimeline';
import PromoteStagesModal from '../../components/barter/PromoteStagesModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LEGEND = [
  { val: '1', label: 'Lead',        bg: '#fee2e2', fg: '#991b1b' },
  { val: '2', label: 'In Progress', bg: '#fed7aa', fg: '#9a3412' },
  { val: '3', label: 'Verified',    bg: '#fef9c3', fg: '#854d0e' },
  { val: '4', label: 'Negotiation', bg: '#bbf7d0', fg: '#166534' },
  { val: '5', label: 'Closed',      bg: '#a7f3d0', fg: '#065f46' },
];

const SHEET_META = {
  marketing: { label: 'MARKETING',  color: '#0d9488', Icon: Briefcase   },
  legal:     { label: 'LEGAL',      color: '#9333ea', Icon: Scale       },
  technical: { label: 'TECHNICAL',  color: '#ea580c', Icon: Wrench      },
  finance:   { label: 'FINANCE',    color: '#16a34a', Icon: IndianRupee },
};

const SHEETS = ['marketing', 'legal', 'technical', 'finance'];

const DealCard = ({ deal, onOpenPromoteModal }) => {
  const statusInfo = STATUS_LEGEND.find(s => String(s.val) === String(deal.status));
  const isLinked  = !!deal.dealGroupId;
  const isClosed  = String(deal.status) === '5';

  return (
    <div
      data-testid={`pipeline-deal-${deal.id}`}
      className={`bg-white border rounded-lg p-3 shadow-sm hover:shadow-md transition-all text-[11px] ${
        isClosed && !isLinked ? 'border-amber-300 ring-1 ring-amber-200' : 'border-stone-200'
      }`}
    >
      {/* Name row */}
      <div className="flex items-start justify-between gap-1 mb-2">
        <div className="font-bold text-stone-800 truncate flex-1 leading-tight" title={deal.customer_name}>
          {deal.customer_name || '(No Name)'}
        </div>
        {isLinked && (
          <span
            data-testid={`linked-badge-${deal.id}`}
            className="shrink-0 text-[8px] bg-emerald-100 text-emerald-700 border border-emerald-300 px-1.5 py-0.5 rounded-full font-bold flex items-center gap-0.5"
          >
            <Link2 className="w-2 h-2" /> LINKED
          </span>
        )}
      </div>

      {/* Meta */}
      <div className="text-stone-500 space-y-0.5 mb-2">
        {deal.dm_name && (
          <div>DM: <span className="text-stone-700 font-medium">{deal.dm_name}</span></div>
        )}
        {deal.barter_pct !== undefined && deal.barter_pct !== null && deal.barter_pct !== '' && (
          <div>Barter: <span className="text-stone-800 font-semibold">{deal.barter_pct}%</span></div>
        )}
        {deal.expected_amount && (
          <div>Expected: <span className="text-emerald-700 font-semibold">
            ₹{Number(deal.expected_amount).toLocaleString('en-IN')}
          </span></div>
        )}
        {deal.area && <div>Area: {deal.area}</div>}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between">
        {statusInfo ? (
          <span
            style={{ backgroundColor: statusInfo.bg, color: statusInfo.fg }}
            className="text-[9px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap"
          >
            {statusInfo.label}
          </span>
        ) : <span />}

        <Button
          data-testid={`promote-btn-${deal.id}`}
          size="sm"
          className={`h-5 px-1.5 text-[9px] font-bold flex items-center gap-0.5 ${
            isLinked
              ? 'bg-stone-100 hover:bg-stone-200 text-stone-600 border border-stone-300'
              : 'bg-amber-500 hover:bg-amber-600 text-white'
          }`}
          onClick={() => onOpenPromoteModal(deal)}
        >
          <Zap className="w-2.5 h-2.5" />
          {isLinked ? 'Manage' : 'Promote…'}
        </Button>
      </div>
    </div>
  );
};

const BarterPipelinePanel = () => {
  const [pipelineData, setPipelineData] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [viewMode, setViewMode]   = useState('kanban'); // 'kanban' | 'timeline'
  const [promoteDeal, setPromoteDeal]   = useState(null);
  const [promoteExisting, setPromoteExisting] = useState([]);

  const token   = localStorage.getItem('crm_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchPipeline = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/barter/pipeline`, { headers });
      setPipelineData(res.data);
    } catch {
      toast.error('Failed to load Barter pipeline');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchPipeline(); }, [fetchPipeline]);

  const handleOpenPromoteModal = (deal) => {
    // Determine which sheets already have a linked record for this group
    const existing = deal.dealGroupId
      ? Object.keys(pipelineData?.groups?.[deal.dealGroupId] || {})
      : [deal.sheet];
    setPromoteDeal(deal);
    setPromoteExisting(existing);
  };

  const totalDeals = pipelineData
    ? Object.values(pipelineData.stats ?? {}).reduce((a, b) => a + b, 0)
    : 0;

  return (
    <>
      <Card data-testid="barter-pipeline-panel" className="border-stone-200">
      <CardHeader className="pb-3 border-b border-stone-100">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base font-bold">
            <span className="w-7 h-7 bg-stone-800 rounded-lg flex items-center justify-center">
              <Link2 className="w-4 h-4 text-white" />
            </span>
            Barter Department · Pipeline Overview
          </CardTitle>

          <div className="flex items-center gap-2 flex-wrap">
            {/* View toggle */}
            <div className="flex items-center border border-stone-200 rounded-lg overflow-hidden" data-testid="pipeline-view-toggle">
              <button
                onClick={() => setViewMode('kanban')}
                data-testid="view-mode-kanban"
                className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium transition-colors ${
                  viewMode === 'kanban'
                    ? 'bg-stone-800 text-white'
                    : 'bg-white text-stone-600 hover:bg-stone-50'
                }`}
              >
                <LayoutGrid className="w-3 h-3" /> Kanban
              </button>
              <button
                onClick={() => setViewMode('timeline')}
                data-testid="view-mode-timeline"
                className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium transition-colors border-l border-stone-200 ${
                  viewMode === 'timeline'
                    ? 'bg-stone-800 text-white'
                    : 'bg-white text-stone-600 hover:bg-stone-50'
                }`}
              >
                <GitBranch className="w-3 h-3" /> Timeline
              </button>
            </div>

            {pipelineData && (
              <div className="flex items-center gap-3 text-xs text-stone-500">
                <span>
                  <span className="font-bold text-stone-800">{totalDeals}</span> total deals
                </span>
                <span>
                  <span className="font-bold text-emerald-700">{pipelineData.total_groups}</span> linked group{pipelineData.total_groups !== 1 ? 's' : ''}
                </span>
              </div>
            )}
            <Button
              size="sm" variant="outline"
              className="h-7 text-xs"
              onClick={fetchPipeline}
              disabled={loading}
              data-testid="pipeline-refresh-btn"
            >
              <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* Sheet stats badges */}
        {pipelineData && (
          <div className="flex flex-wrap gap-2 mt-2">
            {SHEETS.map(s => {
              const m = SHEET_META[s];
              const count = pipelineData.stats?.[s] ?? 0;
              return (
                <div
                  key={s}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold"
                  style={{ borderColor: m.color, backgroundColor: `${m.color}15`, color: m.color }}
                >
                  <m.Icon className="w-3 h-3" />
                  {m.label}
                  <span className="bg-white rounded-full px-1.5 py-0 font-bold text-stone-800">{count}</span>
                </div>
              );
            })}
          </div>
        )}
      </CardHeader>

      <CardContent className="p-4">
        {loading ? (
          <div className="flex items-center justify-center h-40 text-stone-400">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading pipeline…
          </div>
        ) : viewMode === 'timeline' ? (
          /* ── Timeline view ── */
          <TimelineView groups={pipelineData?.groups ?? {}} loading={loading} />
        ) : (
          /* ── Kanban view ── */
          <>
            {/* 4-column Kanban */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {SHEETS.map(s => {
                const m = SHEET_META[s];
                const deals = pipelineData?.by_sheet?.[s] ?? [];
                const linkedCount = deals.filter(d => d.dealGroupId).length;

                return (
                  <div key={s} data-testid={`pipeline-col-${s}`}>
                    {/* Column header */}
                    <div
                      className="flex items-center gap-1.5 mb-3 pb-2 border-b-2"
                      style={{ borderColor: m.color }}
                    >
                      <m.Icon className="w-3.5 h-3.5" style={{ color: m.color }} />
                      <span className="text-xs font-bold" style={{ color: m.color }}>{m.label}</span>
                      <span className="ml-auto text-[10px] bg-stone-100 text-stone-600 font-bold px-1.5 py-0.5 rounded-full">
                        {deals.length}
                      </span>
                    </div>

                    {linkedCount > 0 && (
                      <div className="text-[9px] text-emerald-600 font-semibold mb-2">
                        {linkedCount} linked deal{linkedCount !== 1 ? 's' : ''}
                      </div>
                    )}

                    {/* Deal cards */}
                    <div className="space-y-2 max-h-80 overflow-y-auto pr-0.5">
                      {deals.length === 0 ? (
                        <div className="text-center py-8 text-stone-300 text-[10px] italic">
                          No deals yet
                        </div>
                      ) : (
                        deals.map(deal => (
                          <DealCard
                            key={deal.id}
                            deal={deal}
                            onOpenPromoteModal={handleOpenPromoteModal}
                          />
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Hint strip */}
            <div className="mt-4 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-[10px] text-stone-600">
              Click <span className="font-bold text-amber-600 inline-flex items-center gap-0.5"><Zap className="w-2.5 h-2.5" /> Promote All</span> on any deal to instantly
              clone it across all 4 stages, pre-filling <span className="font-semibold">Customer Name, DM Name, Barter %, Expected Amount</span> and other shared fields.
              Switch to <span className="font-bold text-stone-800 inline-flex items-center gap-0.5"><GitBranch className="w-2.5 h-2.5" /> Timeline</span> view to see full deal history across all stages.
            </div>
          </>
        )}
      </CardContent>
    </Card>

    {/* Promote Stages Modal */}
    <PromoteStagesModal
      deal={promoteDeal}
      isOpen={!!promoteDeal}
      onClose={() => setPromoteDeal(null)}
      onSuccess={() => { fetchPipeline(); setPromoteDeal(null); }}
      existingSheets={promoteExisting}
    />
    </>
  );
};

export default BarterPipelinePanel;
